/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Grid, makeStyles, Typography } from '@material-ui/core';
import { PictureAsPdf } from '@material-ui/icons';
import Minicard from '../../common/Minicard';
import { getFileIconComponent } from '../../../shared/utils/attachmentUtils';

const useStyles = makeStyles(theme => ({
  headerTop: {
    marginTop: theme.spacing(4),
  },
  headerBottom: {
    marginBottom: theme.spacing(4),
  },
  avatar: {
    maxWidth: theme.typography.avatar.maxWidth,
    maxHeight: theme.typography.avatar.maxHeight,
    fontSize: theme.typography.avatar.fontSize,
    backgroundColor: theme.palette.black,
    color: theme.palette.white,
    marginLeft: theme.spacing(1),
  },
}));

export default function SidebarAttachmentCards({ topEntries, attachments, previewObject, loadAttachmentFile }) {
  const classes = useStyles();
  const { t } = useTranslation();

  const component = (
    <>
      {topEntries?.map(entry => (
        <Minicard
          key={entry.id}
          title={t(entry.title)}
          subtitle={entry.subtitle}
          icon={<PictureAsPdf fontSize="small" />}
          isActive={previewObject?.id === entry.id}
          onClick={() => entry.handleEntryClick()}
          aria-controls="filePreview"
        />
      ))}

      <Typography className={classes.headerTop} variant="h3">
        {t('attachments.title')}
      </Typography>

      {attachments.map(attachment => (
        <div key={attachment.category} className={classes.headerBottom}>
          <Typography variant="h4" component="h3">
            <Grid container alignItems="center">
              <Grid item>
                <span>{attachment.category}</span>
              </Grid>
              <Grid item>
                <Avatar className={classes.avatar} variant="rounded">
                  {attachment.files?.length}
                </Avatar>
              </Grid>
            </Grid>
          </Typography>

          {attachment.files?.map((file) => {
            const FileIconComponent = getFileIconComponent(file);
            return (
              <Minicard
                key={file.id}
                title={t(file.attachmentType)}
                subtitle={file.originalFileName}
                icon={<FileIconComponent fontSize="small" />}
                isActive={previewObject?.id === file.id}
                onClick={() => loadAttachmentFile(file)}
                aria-controls="filePreview"
              />
            );
          })}
        </div>
      ))}
    </>
  );

  return useMemo(() => component, [attachments, previewObject, loadAttachmentFile, classes]);
}
